/**
 * Financial Calculation Engine
 * Provides comprehensive financial calculations for the loan management system
 * with decimal precision and automatic balance updates
 */

import { roundToTwoDecimals } from '../models/DebtAccount';
import { calculateArrears } from '../models/PaymentArrangement';

/**
 * Core balance calculation with decimal precision
 * Formula: (debt amount + adjustments + legal fees + interest + debt fees - payments)
 * @param {Object} debtAccount - Debt account object
 * @returns {number} Calculated balance rounded to 2 decimal places
 */
export const calculateAccountBalance = (debtAccount) => {
  const {
    debtAmount = 0,
    adjustments = 0,
    legalFees = 0,
    interest = 0,
    debtFees = 0,
    payments = 0
  } = debtAccount;

  const balance = debtAmount + adjustments + legalFees + interest + debtFees - payments;
  return roundToTwoDecimals(balance);
};

/**
 * Process a payment and update account balance automatically
 * @param {Object} debtAccount - Current debt account
 * @param {number} paymentAmount - Payment amount to process
 * @param {Date} paymentDate - Date of payment (defaults to now)
 * @returns {Object} Updated debt account with new balance
 */
export const processPayment = (debtAccount, paymentAmount, paymentDate = new Date()) => {
  const amount = roundToTwoDecimals(parseFloat(paymentAmount) || 0);
  
  if (amount <= 0) {
    throw new Error('Payment amount must be greater than 0');
  }

  const newPayments = roundToTwoDecimals(debtAccount.payments + amount);
  const newBalance = calculateAccountBalance({
    ...debtAccount,
    payments: newPayments
  });

  return {
    ...debtAccount,
    payments: newPayments,
    balance: newBalance,
    lastPaymentDate: paymentDate,
    lastPaymentAmount: amount,
    updatedAt: new Date()
  };
};

/**
 * Calculate arrears for missed payments based on payment arrangement
 * @param {Object} paymentArrangement - Payment arrangement object
 * @param {Array} missedPaymentDates - Array of missed payment dates
 * @param {Date} currentDate - Current date for calculation (defaults to now)
 * @returns {number} Total arrears amount
 */
export const calculateMissedPaymentArrears = (paymentArrangement, missedPaymentDates = [], currentDate = new Date()) => {
  if (!paymentArrangement.isActive) {
    return 0;
  }

  // Calculate based on missed payment dates
  if (missedPaymentDates.length > 0) {
    return calculateArrears(paymentArrangement, missedPaymentDates);
  }

  // Calculate based on overdue status
  const daysOverdue = Math.floor((currentDate - paymentArrangement.nextDueDate) / (1000 * 60 * 60 * 24));
  
  if (daysOverdue <= 0) {
    return 0; // Not overdue
  }

  // Calculate number of missed payments based on frequency
  const missedPayments = calculateMissedPaymentCount(paymentArrangement, currentDate);
  return roundToTwoDecimals(missedPayments * paymentArrangement.amount);
};

/**
 * Calculate number of missed payments based on arrangement frequency
 * @param {Object} paymentArrangement - Payment arrangement object
 * @param {Date} currentDate - Current date for calculation
 * @returns {number} Number of missed payments
 */
export const calculateMissedPaymentCount = (paymentArrangement, currentDate = new Date()) => {
  if (!paymentArrangement.isActive || paymentArrangement.nextDueDate >= currentDate) {
    return 0;
  }

  const daysDiff = Math.floor((currentDate - paymentArrangement.nextDueDate) / (1000 * 60 * 60 * 24));
  
  switch (paymentArrangement.frequency) {
    case 'weekly':
      return Math.floor(daysDiff / 7) + 1;
    case 'bi-weekly':
      return Math.floor(daysDiff / 14) + 1;
    case 'monthly':
      return Math.floor(daysDiff / 30) + 1;
    case 'quarterly':
      return Math.floor(daysDiff / 90) + 1;
    case 'annually':
      return Math.floor(daysDiff / 365) + 1;
    default:
      return Math.floor(daysDiff / 30) + 1; // Default to monthly
  }
};

/**
 * Track settlement amounts and calculate settlement options
 * @param {Object} debtAccount - Debt account object
 * @param {number} settlementPercentage - Settlement percentage (0-100)
 * @returns {Object} Settlement calculation details
 */
export const calculateSettlementAmount = (debtAccount, settlementPercentage = 50) => {
  const currentBalance = calculateAccountBalance(debtAccount);
  const settlementAmount = roundToTwoDecimals(currentBalance * (settlementPercentage / 100));
  const savings = roundToTwoDecimals(currentBalance - settlementAmount);
  const savingsPercentage = currentBalance > 0 ? roundToTwoDecimals((savings / currentBalance) * 100) : 0;

  return {
    currentBalance,
    settlementAmount,
    savings,
    savingsPercentage,
    settlementPercentage
  };
};

/**
 * Calculate total financial summary for a client across all accounts
 * @param {Array} debtAccounts - Array of debt accounts for the client
 * @param {Array} paymentArrangements - Array of payment arrangements for the client
 * @returns {Object} Comprehensive financial summary
 */
export const calculateClientFinancialSummary = (debtAccounts = [], paymentArrangements = []) => {
  const totalDebtAmount = debtAccounts.reduce((sum, account) => sum + account.debtAmount, 0);
  const totalAdjustments = debtAccounts.reduce((sum, account) => sum + (account.adjustments || 0), 0);
  const totalLegalFees = debtAccounts.reduce((sum, account) => sum + (account.legalFees || 0), 0);
  const totalInterest = debtAccounts.reduce((sum, account) => sum + (account.interest || 0), 0);
  const totalDebtFees = debtAccounts.reduce((sum, account) => sum + (account.debtFees || 0), 0);
  const totalPayments = debtAccounts.reduce((sum, account) => sum + (account.payments || 0), 0);
  const totalBalance = debtAccounts.reduce((sum, account) => sum + calculateAccountBalance(account), 0);

  // Calculate arrangement-related amounts
  const totalArrangementAmount = paymentArrangements
    .filter(arr => arr.isActive)
    .reduce((sum, arr) => sum + arr.amount, 0);
  
  const totalArrears = paymentArrangements.reduce((sum, arr) => {
    return sum + calculateMissedPaymentArrears(arr);
  }, 0);

  // Find most recent payment
  const lastPayment = debtAccounts
    .filter(account => account.lastPaymentDate)
    .sort((a, b) => new Date(b.lastPaymentDate) - new Date(a.lastPaymentDate))[0];

  return {
    totalDebtAmount: roundToTwoDecimals(totalDebtAmount),
    totalAdjustments: roundToTwoDecimals(totalAdjustments),
    totalLegalFees: roundToTwoDecimals(totalLegalFees),
    totalInterest: roundToTwoDecimals(totalInterest),
    totalDebtFees: roundToTwoDecimals(totalDebtFees),
    totalPayments: roundToTwoDecimals(totalPayments),
    totalBalance: roundToTwoDecimals(totalBalance),
    totalArrangementAmount: roundToTwoDecimals(totalArrangementAmount),
    totalArrears: roundToTwoDecimals(totalArrears),
    accountCount: debtAccounts.length,
    activeArrangementCount: paymentArrangements.filter(arr => arr.isActive).length,
    lastPaymentDate: lastPayment?.lastPaymentDate || null,
    lastPaymentAmount: lastPayment?.lastPaymentAmount || 0
  };
};

/**
 * Calculate interest charges based on principal and rate
 * @param {number} principal - Principal amount
 * @param {number} annualRate - Annual interest rate (as percentage, e.g., 5.5 for 5.5%)
 * @param {number} days - Number of days to calculate interest for
 * @returns {number} Interest amount
 */
export const calculateInterest = (principal, annualRate, days = 365) => {
  const dailyRate = annualRate / 100 / 365;
  const interest = principal * dailyRate * days;
  return roundToTwoDecimals(interest);
};

/**
 * Apply an adjustment to a debt account
 * @param {Object} debtAccount - Current debt account
 * @param {number} adjustmentAmount - Adjustment amount (can be negative)
 * @param {string} reason - Reason for adjustment
 * @returns {Object} Updated debt account
 */
export const applyAccountAdjustment = (debtAccount, adjustmentAmount, reason = '') => {
  const amount = roundToTwoDecimals(parseFloat(adjustmentAmount) || 0);
  const newAdjustments = roundToTwoDecimals(debtAccount.adjustments + amount);
  const newBalance = calculateAccountBalance({
    ...debtAccount,
    adjustments: newAdjustments
  });

  return {
    ...debtAccount,
    adjustments: newAdjustments,
    balance: newBalance,
    updatedAt: new Date(),
    lastAdjustment: {
      amount,
      reason,
      date: new Date()
    }
  };
};

/**
 * Calculate payment allocation across multiple debt components
 * @param {Object} debtAccount - Debt account
 * @param {number} paymentAmount - Payment amount to allocate
 * @param {Object} allocationRules - Rules for payment allocation priority
 * @returns {Object} Payment allocation breakdown
 */
export const calculatePaymentAllocation = (debtAccount, paymentAmount, allocationRules = {}) => {
  const amount = roundToTwoDecimals(parseFloat(paymentAmount) || 0);
  let remainingAmount = amount;
  
  const allocation = {
    legalFees: 0,
    interest: 0,
    debtFees: 0,
    principal: 0,
    total: amount
  };

  // Default allocation priority: legal fees, interest, debt fees, then principal
  const priority = allocationRules.priority || ['legalFees', 'interest', 'debtFees', 'principal'];
  
  for (const component of priority) {
    if (remainingAmount <= 0) break;
    
    let outstandingAmount = 0;
    switch (component) {
      case 'legalFees':
        outstandingAmount = Math.max(0, debtAccount.legalFees - (allocation.legalFees || 0));
        break;
      case 'interest':
        outstandingAmount = Math.max(0, debtAccount.interest - (allocation.interest || 0));
        break;
      case 'debtFees':
        outstandingAmount = Math.max(0, debtAccount.debtFees - (allocation.debtFees || 0));
        break;
      case 'principal':
        outstandingAmount = Math.max(0, debtAccount.debtAmount + debtAccount.adjustments - (allocation.principal || 0));
        break;
    }
    
    const allocationAmount = Math.min(remainingAmount, outstandingAmount);
    allocation[component] = roundToTwoDecimals(allocationAmount);
    remainingAmount = roundToTwoDecimals(remainingAmount - allocationAmount);
  }

  return allocation;
};

/**
 * Validate financial calculation inputs
 * @param {Object} inputs - Financial calculation inputs
 * @returns {Object} Validation result
 */
export const validateFinancialInputs = (inputs) => {
  const errors = [];
  
  // Check for required numeric fields
  const numericFields = ['debtAmount', 'adjustments', 'legalFees', 'interest', 'debtFees', 'payments'];
  
  numericFields.forEach(field => {
    const value = inputs[field];
    if (value !== undefined && value !== null) {
      if (isNaN(value)) {
        errors.push(`${field} must be a valid number`);
      }
      
      // Check decimal precision
      const decimalPlaces = (value.toString().split('.')[1] || '').length;
      if (decimalPlaces > 2) {
        errors.push(`${field} cannot have more than 2 decimal places`);
      }
    }
  });
  
  // Validate payment amounts are positive
  if (inputs.paymentAmount !== undefined && inputs.paymentAmount <= 0) {
    errors.push('Payment amount must be greater than 0');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Format currency values for display
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: USD)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

/**
 * Calculate payment schedule for an arrangement
 * @param {Object} paymentArrangement - Payment arrangement
 * @param {number} numberOfPayments - Number of future payments to calculate
 * @returns {Array} Array of payment schedule objects
 */
export const calculatePaymentSchedule = (paymentArrangement, numberOfPayments = 12) => {
  const schedule = [];
  let currentDate = new Date(paymentArrangement.nextDueDate);
  
  for (let i = 0; i < numberOfPayments; i++) {
    schedule.push({
      paymentNumber: i + 1,
      dueDate: new Date(currentDate),
      amount: paymentArrangement.amount,
      formattedAmount: formatCurrency(paymentArrangement.amount),
      formattedDate: currentDate.toLocaleDateString()
    });
    
    // Calculate next payment date based on frequency
    switch (paymentArrangement.frequency) {
      case 'weekly':
        currentDate.setDate(currentDate.getDate() + 7);
        break;
      case 'bi-weekly':
        currentDate.setDate(currentDate.getDate() + 14);
        break;
      case 'monthly':
        currentDate.setMonth(currentDate.getMonth() + 1);
        break;
      case 'quarterly':
        currentDate.setMonth(currentDate.getMonth() + 3);
        break;
      case 'annually':
        currentDate.setFullYear(currentDate.getFullYear() + 1);
        break;
      default:
        currentDate.setMonth(currentDate.getMonth() + 1);
    }
  }
  
  return schedule;
};